/**
 * Run labels (dashboard change, 2026-09-08).
 *
 * The ONE builder for the `run_label` object every run payload carries (Run,
 * Queue/hold, per-route runs, multi-run bodies), plus the read side: which
 * label / phase / percent a run tile, the dashboard hero and a Multi-project
 * runs row show, with graceful fallbacks for older backends and older runs
 * that never got a label.
 *
 * Contract (sent with every payload):
 *   {
 *     route: "SR143s1-1",          // the ROUTE file name as uploaded (leads)
 *     segment_index: 1,            // secondary: lane-XML segment counter, else the
 *                                  // route's ordinal within the run
 *     mp_start: 0, mp_end: 12.3,   // from the state LRS when the route was loaded
 *                                  // from it (path.lrs), else null
 *     miles: 12.3,                 // rsa_xml_file_cache miles (routesPerFileMileage)
 *     range_source: "lrs" | "mileage" | null,
 *     directions: ["P", "N"],
 *     analyses: ["SSD", ...],
 *     display: "SR143s1-1 · MP 0–12.3"   // or "SR143s1-1 · 12.3 mi"
 *   }
 *
 * Backend read side (task-list rows, the progress GET, queue rows): `run_label`
 * (object|null), `phase` (queued|preprocess|analysis|deliverables|done|failed),
 * `phase_label`, `overall_percent` (0..100). Used when present; otherwise the
 * client derivation (3-band model in AnalysisDashboard.jsx) is the fallback.
 *
 * Pure: no React, no network.
 */
import {
  TASK_OPTION_TO_FOLDER_CODE,
  RSA_FOLDER_CODE_TO_SUFFIX,
} from "./rsaTaskCatalog";
import { parseLaneXmlName } from "../multiRouteRun";

export const PHASE_KEYS = [
  "queued",
  "preprocess",
  "analysis",
  "deliverables",
  "done",
  "failed",
];

/** Display label + colour tone per phase (tones map to CSS classes). */
export const PHASES = {
  queued: { key: "queued", label: "Queued", tone: "queued" },
  preprocess: { key: "preprocess", label: "Pre-process", tone: "preprocess" },
  analysis: { key: "analysis", label: "Analysis", tone: "analysis" },
  deliverables: { key: "deliverables", label: "Deliverables", tone: "deliverables" },
  done: { key: "done", label: "Done", tone: "done" },
  failed: { key: "failed", label: "Failed", tone: "failed" },
};

// null / undefined / "" → null (Number(null) is 0, which would read as MP 0).
const finite = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** 12.30 → "12.3", 12 → "12", 0.05 → "0.1" (one decimal, trailing .0 dropped). */
export const formatMilepost = (value) => {
  const n = finite(value);
  if (n == null) return null;
  const s = n.toFixed(1);
  return s.endsWith(".0") ? s.slice(0, -2) : s;
};

/** "MP 0–12.3" (en dash) or null when either end is unknown. */
export const formatMilepostRange = (start, end) => {
  const a = formatMilepost(start);
  const b = formatMilepost(end);
  if (a == null || b == null) return null;
  return `MP ${a}–${b}`;
};

/** "12.3 mi" or null. */
export const formatMiles = (miles) => {
  const s = formatMilepost(miles);
  return s == null ? null : `${s} mi`;
};

/**
 * Mileposts of a path built from the state LRS. The binding lives on the
 * path object as `path.lrs`, written by PathsPanels/LrsRoutePanel.jsx:
 *   v2 (two points): { v: 2, start: { routeKey, measure, … }, end: { measure, … } }
 *   v1 (flat):       { routeKey, mpStart, mpEnd, … }
 * Map-click routes carry lrs: null → null here.
 */
export const milepostsFromLrs = (lrs) => {
  if (!lrs || typeof lrs !== "object") return null;
  let start;
  let end;
  if (lrs.v === 2) {
    start = finite(lrs.start?.measure);
    end = finite(lrs.end?.measure);
  } else {
    start = finite(lrs.mpStart);
    end = finite(lrs.mpEnd);
  }
  if (start == null || end == null) return null;
  return { mp_start: start, mp_end: end };
};

/** "Stopping Sight Distance (SSD)" → "SSD"; a bare "SSD" passes through. */
export const analysisCodeForTask = (taskName) => {
  const name = String(taskName ?? "").trim();
  if (!name) return null;
  const paren = name.match(/\(([A-Z]{3})\)\s*$/);
  if (paren) return paren[1];
  const folderCode = TASK_OPTION_TO_FOLDER_CODE[name];
  if (folderCode && RSA_FOLDER_CODE_TO_SUFFIX[folderCode]) {
    return RSA_FOLDER_CODE_TO_SUFFIX[folderCode];
  }
  if (/^[A-Z]{3}$/.test(name)) return name;
  return null;
};

/** Unique 3-letter codes, order preserved. */
export const analysisCodesForTasks = (taskNames) =>
  Array.from(
    new Set((taskNames || []).map(analysisCodeForTask).filter(Boolean)),
  );

/** "LasSeparationPath_SR143s1-1.json" / "SR143s1-1.xml" → "SR143s1-1". */
export const routeNameFromFileName = (fileName) =>
  String(fileName ?? "")
    .trim()
    .replace(/^LasSeparationPath_/i, "")
    .replace(/\.(xml|json)$/i, "")
    .trim();

const DIRECTION_RE = /^[PN]$/i;

/**
 * What a lane-XML file name says about its route: the platform writes
 * `<routeToken>_<STATE>_<routeNumber>_<segment>_<lanePosition>_<direction>.xml`
 * (buildLaneXmlFilename in RSAAnalysis.js). A user-uploaded route XML
 * ("SR143s1-1.xml") has none of that: direction/segment come back null.
 */
export const laneFactsFromFileName = (fileName) => {
  const parsed = parseLaneXmlName(fileName);
  const direction = String(parsed.direction || "").toUpperCase();
  const segment = finite(parsed.segment);
  const isLaneXml =
    Boolean(parsed.lane) && DIRECTION_RE.test(direction) && segment != null;
  // The Route token is everything before the trailing 5 parts
  // (<STATE>_<routeNumber>_<segment>_<lanePosition>_<direction>) — the path
  // label the lane was generated from ("SR143s1-1"). Absent when the file has
  // exactly 5 parts (no token) or is not a lane XML at all.
  let routeToken = null;
  if (isLaneXml) {
    const parts = String(fileName || "")
      .replace(/\.xml$/i, "")
      .split("_")
      .filter((p) => p !== "");
    if (parts.length >= 6) routeToken = parts.slice(0, -5).join("_");
  }
  return {
    direction: isLaneXml ? direction : null,
    segmentIndex: isLaneXml ? segment : null,
    routeToken: routeToken || null,
  };
};

/**
 * One route entry for buildRunLabel from a route XML row of the run form
 * (`routesXmlFiles`): the file name leads; mileposts come from the path the
 * XML was written from (rsaPathsHook.xmlToJsonMap[name].pathId → path.lrs);
 * miles from the mileage cache keyed by file name.
 */
export const routeEntryFromXmlFile = (
  file,
  { xmlToJsonMap = {}, paths = [], perFileMileage = {}, normalizeFileNameKey = null } = {},
) => {
  const fileName = String(file?.name ?? "").trim();
  if (!fileName) return null;
  const mapEntry = xmlToJsonMap?.[fileName] || null;
  const path =
    (mapEntry?.pathId && (paths || []).find((p) => p?.id === mapEntry.pathId)) ||
    null;
  const normKey = normalizeFileNameKey ? normalizeFileNameKey(fileName) : null;
  const miles = finite(
    perFileMileage?.[fileName] ?? (normKey ? perFileMileage?.[normKey] : undefined),
  );
  const facts = laneFactsFromFileName(fileName);
  return {
    // A platform-generated lane XML is labelled by its Route token (the path
    // it came from); a user-uploaded route XML keeps its file name as uploaded.
    name: facts.routeToken || mapEntry?.pathLabel || routeNameFromFileName(fileName),
    lrs: path?.lrs ?? null,
    miles,
    directions: facts.direction ? [facts.direction] : [],
    segmentIndex: facts.segmentIndex,
  };
};

/**
 * One route entry for a multi-route run spec (multiRouteRun.buildRunSpecs):
 * the Route is the path label embedded in the JSON name; its lane XMLs
 * (xmlsByTask) give directions and cached miles; the matching path gives the
 * LRS mileposts.
 */
export const routeEntryFromRunSpec = (
  spec,
  { paths = [], perFileMileage = {}, perFileMileageById = {}, normalizeFileNameKey = null, normalizeId = null } = {},
) => {
  const name = routeNameFromFileName(spec?.jsonName);
  if (!name) return null;
  const xmls = [];
  const seen = new Set();
  for (const list of Object.values(spec?.xmlsByTask || {})) {
    for (const xml of list || []) {
      const key = String(xml?.googleDriveId || xml?.name || "");
      if (!key || seen.has(key)) continue;
      seen.add(key);
      xmls.push(xml);
    }
  }
  const directions = [];
  let segmentIndex = null;
  let miles = null;
  for (const xml of xmls) {
    const facts = laneFactsFromFileName(xml?.name);
    if (facts.direction && !directions.includes(facts.direction)) {
      directions.push(facts.direction);
    }
    if (segmentIndex == null && facts.segmentIndex != null) {
      segmentIndex = facts.segmentIndex;
    }
    const idKey = normalizeId ? normalizeId(xml?.googleDriveId) : xml?.googleDriveId;
    const normKey = normalizeFileNameKey ? normalizeFileNameKey(xml?.name) : null;
    const m = finite(
      (idKey ? perFileMileageById?.[idKey] : undefined) ??
        perFileMileage?.[xml?.name] ??
        (normKey ? perFileMileage?.[normKey] : undefined),
    );
    if (m != null) miles = (miles ?? 0) + m;
  }
  const path =
    (paths || []).find(
      (p) => routeNameFromFileName(`LasSeparationPath_${sanitizeLabel(p?.label)}.json`) === name,
    ) || null;
  return {
    name,
    lrs: path?.lrs ?? null,
    miles,
    directions,
    segmentIndex,
  };
};

// Same rule as PathsPanels/useRsaPaths.sanitizePathLabel (kept local so this
// module stays free of the paths hook's imports).
const sanitizeLabel = (label) => {
  let s = String(label ?? "").trim();
  s = s.replace(/[^A-Za-z0-9_-]+/g, "_").replace(/_+/g, "_").replace(/^_+|_+$/g, "");
  return s;
};

/** "SR143s1-1 · MP 0–12.3" / "SR143s1-1 · 12.3 mi" / "SR143s1-1" (+ " +2" for extra routes). */
export const runLabelDisplay = ({ route, mp_start, mp_end, miles, extra = 0 } = {}) => {
  const name = String(route ?? "").trim();
  if (!name) return "";
  const range = formatMilepostRange(mp_start, mp_end) || formatMiles(miles);
  const text = range ? `${name} · ${range}` : name;
  return extra > 0 ? `${text} +${extra}` : text;
};

/**
 * Build the `run_label` object.
 *
 * @param {object} input
 * @param {Array}  input.routes       [{ name, lrs, miles, directions, segmentIndex }] — the
 *                                    selected route(s) of THIS run, first one leads
 * @param {string[]} input.analyses   3-letter codes (or task names; both accepted)
 * @param {string[]} input.folderNames folder flow (no routes): the ticked folders' names
 * @param {number|null} input.segmentIndex fallback ordinal (per-route runs pass i+1)
 * @returns {object|null} null only when there is nothing at all to name
 */
export const buildRunLabel = ({
  routes = [],
  analyses = [],
  folderNames = [],
  segmentIndex = null,
} = {}) => {
  const codes = analysisCodesForTasks(analyses);
  const list = (routes || []).filter((r) => r && String(r.name || "").trim());

  if (list.length === 0) {
    const folders = (folderNames || [])
      .map((f) => String(f ?? "").trim())
      .filter(Boolean);
    if (folders.length === 0 && codes.length === 0) return null;
    const route = folders[0] || null;
    return {
      route,
      segment_index: null,
      mp_start: null,
      mp_end: null,
      miles: null,
      range_source: null,
      directions: [],
      analyses: codes,
      display: route
        ? folders.length > 1
          ? `${route} +${folders.length - 1}`
          : route
        : codes.join(", "),
    };
  }

  const primary = list[0];
  const mp = milepostsFromLrs(primary.lrs);
  let miles = null;
  for (const r of list) {
    const m = finite(r.miles);
    if (m != null) miles = (miles ?? 0) + m;
  }
  const directions = [];
  for (const r of list) {
    for (const d of r.directions || []) {
      const dir = String(d || "").toUpperCase();
      if (DIRECTION_RE.test(dir) && !directions.includes(dir)) directions.push(dir);
    }
  }
  const route = String(primary.name).trim();
  // "+N" counts OTHER routes in the run — two lanes of the same route (P + N)
  // are one route, not two.
  const distinctRoutes = new Set(list.map((r) => String(r.name).trim()));
  const label = {
    route,
    segment_index: finite(primary.segmentIndex) ?? finite(segmentIndex),
    mp_start: mp ? mp.mp_start : null,
    mp_end: mp ? mp.mp_end : null,
    miles,
    range_source: mp ? "lrs" : miles != null ? "mileage" : null,
    directions,
    analyses: codes,
  };
  label.display = runLabelDisplay({ ...label, extra: distinctRoutes.size - 1 });
  return label;
};

// ---------------------------------------------------------------------------
// Read side: what a run tile / row shows.
// ---------------------------------------------------------------------------

const isLabel = (v) => v && typeof v === "object" && !Array.isArray(v);

/** The run's label from wherever the backend put it (task row, metadata, launch payload, queue entry). */
export const runLabelOf = (run, entry = null) => {
  const candidates = [
    run?.run_label,
    run?.metadata?.run_label,
    run?.metadata?.launch_payload?.run_label,
    entry?.run_label,
  ];
  for (const c of candidates) if (isLabel(c)) return c;
  return null;
};

/** Analysis codes for a run without a label (older runs). */
export const analysisTypesOf = (run, entry = null) => {
  const fromList = (list) =>
    Array.isArray(list) ? analysisCodesForTasks(list) : [];
  const sources = [
    run?.analysis_types,
    run?.metadata?.analysis_types,
    entry?.analysis_types,
    run?.metadata?.launch_payload?.selected_task_names,
    run?.services,
  ];
  for (const s of sources) {
    const codes = fromList(s);
    if (codes.length) return codes;
  }
  const single = analysisCodeForTask(
    run?.analysis_type || run?.metadata?.analysis_type || run?.metadata?.launch_payload?.analysis_type,
  );
  return single ? [single] : [];
};

export const projectNameOf = (run, entry = null) => {
  const name = String(
    run?.project_name || entry?.project_name || run?.name || "",
  ).trim();
  if (name) return name;
  const id = run?.project_id ?? entry?.project_id;
  return id != null && id !== "" ? `Project ${String(id).replace(/^project_/, "")}` : "Project";
};

/** "both directions" / "direction P" / null. */
export const directionsText = (directions) => {
  const dirs = (directions || []).map((d) => String(d).toUpperCase());
  const hasP = dirs.includes("P");
  const hasN = dirs.includes("N");
  if (hasP && hasN) return "both directions";
  if (hasP) return "direction P";
  if (hasN) return "direction N";
  return null;
};

/** Second line of a labelled tile: "Route SR143s1-1 · both directions · 12.3 mi". */
export const runLabelSubtitle = (label) => {
  if (!isLabel(label) || !label.route) return "";
  const parts = [`Route ${label.route}`];
  const dirs = directionsText(label.directions);
  if (dirs) parts.push(dirs);
  let miles = finite(label.miles);
  if (miles == null) {
    const a = finite(label.mp_start);
    const b = finite(label.mp_end);
    if (a != null && b != null) miles = Math.abs(b - a);
  }
  const milesText = formatMiles(miles);
  if (milesText) parts.push(milesText);
  return parts.join(" · ");
};

const PHASE_ALIASES = {
  queued: "queued",
  held: "queued",
  pending: "queued",
  preprocess: "preprocess",
  "pre-process": "preprocess",
  pre_process: "preprocess",
  preprocessing: "preprocess",
  file_transfer: "preprocess",
  analysis: "analysis",
  rsa_analysis: "analysis",
  deliverables: "deliverables",
  final_reports: "deliverables",
  reports: "deliverables",
  done: "done",
  completed: "done",
  succeeded: "done",
  failed: "failed",
  error: "failed",
};

/** Normalise a backend / step-name phase to one of PHASE_KEYS (or null). */
export const normalizePhase = (value) => {
  const key = String(value ?? "").trim().toLowerCase();
  return PHASE_ALIASES[key] || null;
};

/** Server phase for a run: progress GET wins, then the task row, then the queue entry. */
export const serverPhase = (run, progress = null, entry = null) => {
  for (const src of [progress, run, entry]) {
    const p = normalizePhase(src?.phase);
    if (p) {
      const label =
        String(src?.phase_label || "").trim() || PHASES[p].label;
      return { phase: p, label };
    }
  }
  return null;
};

/** Server overall percent (contract `overall_percent`), or null when absent. */
export const serverOverallPercent = (run, progress = null, entry = null) => {
  for (const src of [progress, run, entry]) {
    if (src && src.overall_percent != null) {
      const n = finite(src.overall_percent);
      if (n != null) return Math.max(0, Math.min(100, Math.round(n)));
    }
  }
  return null;
};

/** Run id → "…1134-4595" (the time + suffix of the stamped id; last 9 chars otherwise). */
export const runIdTail = (runId) => {
  const id = String(runId ?? "").trim();
  if (!id) return "";
  const stamped = id.match(/\d{6}-(\d{4}-[a-z0-9]+)$/i);
  if (stamped) return `…${stamped[1]}`;
  return id.length > 9 ? `…${id.slice(-9)}` : id;
};

/**
 * Everything a tile / row header needs, with the fallbacks applied:
 *   title    label.display, else "<project name> · <analysis types>"
 *   subtitle "Route … · both directions · 12.3 mi", else "Project <id>"
 *   analyses codes (label first, then the run's own fields)
 *   phase    { phase, label } from the server, or null (caller derives)
 *   percent  server overall_percent, or null (caller derives)
 *   hasLabel whether a backend/payload label exists
 */
export const describeRun = (run, progress = null, entry = null) => {
  const label = runLabelOf(run, entry);
  const analyses =
    label && Array.isArray(label.analyses) && label.analyses.length
      ? analysisCodesForTasks(label.analyses)
      : analysisTypesOf(run, entry);
  const projectName = projectNameOf(run, entry);
  const projectId = run?.project_id ?? entry?.project_id ?? null;
  const display = label ? String(label.display || runLabelDisplay(label) || "").trim() : "";
  const title =
    display ||
    (analyses.length ? `${projectName} · ${analyses.join(", ")}` : projectName);
  const subtitle = label
    ? runLabelSubtitle(label)
    : projectId != null
      ? `Project ${String(projectId).replace(/^project_/, "")}`
      : "";
  return {
    label,
    hasLabel: Boolean(display),
    title,
    subtitle,
    analyses,
    projectName,
    projectId,
    phase: serverPhase(run, progress, entry),
    percent: serverOverallPercent(run, progress, entry),
    idTail: runIdTail(run?.full_run_id || run?.run_id || entry?.run_id),
  };
};

/** Multi-project row name: "<Project name> · <route> · MP a–b" (label) or "<Project name> · SSD, ISD". */
export const queueRowTitle = (entry) => {
  const d = describeRun(null, null, entry);
  const projectName = projectNameOf(null, entry);
  if (d.hasLabel) return `${projectName} · ${d.title}`;
  return d.title;
};
